import { useConfig } from "@/context/ConfigContext";
import { useEffect, useState } from "react";

/**
 * Hook untuk mengambil order history list
 * @param {Object} options - Options untuk fetch
 * @param {string} options.contact_type - Contact type: 'whatsapp', 'sms', 'call', 'email'
 * @param {string} options.phone - Phone number
 * @param {string} options.email - Email
 * @param {boolean} options.enabled - Enable/disable fetch (default: true)
 */
export function useOrderHistory(options = {}) {
  const { contact_type, phone, email, enabled = true } = options;
  const { config, configData } = useConfig();
  const [orderHistory, setOrderHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const isEmail = contact_type === "email";
    const contactValue = isEmail ? email : phone;

    if (
      !enabled ||
      !configData?.team_id ||
      !config?.api?.baseUrl ||
      !contact_type ||
      !contactValue
    ) {
      return;
    }

    const fetchOrderHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        const proxyUrl = `/api/orders?action=list`;

        console.log("[useOrderHistory] Fetching order history:", {
          proxyUrl,
          contact_type,
          contactValue,
        });

        const requestBody = isEmail
          ? { email: contactValue }
          : {
              contact_type,
              phone: contactValue.replace(/[^0-9]/g, ""), // Remove non-numeric
            };

        const response = await fetch(proxyUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(requestBody),
          cache: "no-cache",
        });

        if (!response.ok) {
          let errorData;
          let errorText;

          try {
            errorText = await response.text();
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            errorData = {
              message: errorText || response.statusText,
              status: response.status,
            };
          }

          console.error("[useOrderHistory] API Error:", errorData);
          throw new Error(
            errorData.message ||
              errorData.error ||
              errorText ||
              response.statusText
          );
        }

        const data = await response.json();
        setOrderHistory(data.data || data);
      } catch (err) {
        console.error("[useOrderHistory] Error fetching order history:", {
          message: err.message,
          stack: err.stack,
          teamId: configData?.team_id,
          contact_type,
          contactValue,
        });
        setError(err.message || "Gagal mengambil order history");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderHistory();
  }, [
    configData?.team_id,
    config?.api?.baseUrl,
    contact_type,
    phone,
    email,
    enabled,
  ]);

  return {
    orderHistory,
    loading,
    error,
  };
}

/**
 * Hook untuk mengambil order detail by UUID
 * @param {Object} options - Options untuk fetch
 * @param {string} options.uuid - Order UUID
 * @param {boolean} options.enabled - Enable/disable fetch (default: true)
 */
export function useOrderDetail(options = {}) {
  const {
    uuid,
    enabled = true,
    endpoint,
    phone,
    email,
    emailSignature,
    refreshKey,
  } = options;
  const { config, configData } = useConfig();
  const [orderDetail, setOrderDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const hasEndpoint = !!endpoint;
    if (
      !enabled ||
      !uuid ||
      (!hasEndpoint && (!configData?.team_id || !config?.api?.baseUrl))
    ) {
      return;
    }

    const fetchOrderDetail = async () => {
      try {
        setLoading(true);
        setError(null);

        const searchParams = new URLSearchParams();
        if (email) {
          searchParams.set("email", email);
        }
        if (phone) {
          searchParams.set("phone", String(phone).replace(/[^0-9]/g, ""));
        }
        if (emailSignature) {
          searchParams.set("email_signature", emailSignature);
        }

        const queryString = searchParams.toString();
        const proxyUrl =
          endpoint ||
          `/api/orders?action=detail&uuid=${uuid}${
            queryString ? `&${queryString}` : ""
          }`;

        console.log("[useOrderDetail] Fetching order detail:", {
          proxyUrl,
          uuid,
        });

        const response = await fetch(proxyUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          cache: "no-cache",
        });

        if (!response.ok) {
          let errorData;
          let errorText;

          try {
            errorText = await response.text();
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            errorData = {
              message: errorText || response.statusText,
              status: response.status,
            };
          }

          console.error("[useOrderDetail] API Error:", errorData);
          throw new Error(
            errorData.message ||
              errorData.error ||
              errorText ||
              response.statusText
          );
        }

        const data = await response.json();
        setOrderDetail(data.data || data);
      } catch (err) {
        console.error("[useOrderDetail] Error fetching order detail:", {
          message: err.message,
          stack: err.stack,
          teamId: configData?.team_id,
          uuid,
        });
        setError(err.message || "Gagal mengambil order detail");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetail();
  }, [
    configData?.team_id,
    config?.api?.baseUrl,
    uuid,
    enabled,
    endpoint,
    phone,
    email,
    emailSignature,
    refreshKey,
  ]);

  return {
    orderDetail,
    loading,
    error,
  };
}
